import { PageType } from '../types/cmsTypes';
import { InternalArticle, RelevantArticle } from '../types/RelevantArticle';
import { BrandId } from './override';
import { getCmsPageLink } from './routing';

export type RelevantArticleLinkContext = {
  brand: BrandId;
  langPrefix: string;
};

type InternalTarget = Pick<InternalArticle, 'slug' | 'section' | 'urlPrefix'>;

/**
 * URL of the internal Article / Page a relevant-article entry points at.
 *
 * Every consumer built this same `getCmsPageLink` call by hand — the AIO
 * module, both branches of `RelevantArticle` and `NestedContent`'s FAQ — so it
 * lives here once.
 */
export const getInternalArticleUrl = (
  target: InternalTarget,
  pageType: Extract<PageType, 'article' | 'page'>,
  { brand, langPrefix }: RelevantArticleLinkContext,
): string =>
  getCmsPageLink({
    brand,
    langPrefix,
    pageType,
    slug: target.slug ?? '',
    vertical: target.section,
    urlPrefix: target.urlPrefix,
  });

export type RelevantArticleCard = {
  url: string;
  title: string;
  subtitle: string | null;
};

/**
 * Card data for one relevant-article entry: an external link first, then an
 * internal article, then an internal page — the first branch that has data
 * wins, and an entry with none of them is dropped by the caller's filter.
 *
 * Extracted unchanged from two byte-identical copies (`RelevantArticle`
 * standard mode and `NestedContent`'s FAQ), including its quirks: an external
 * entry takes its *title* from `subtitle` and its *subtitle* from
 * `description`, and a missing url/title becomes `''` rather than dropping the
 * card. Callers depend on that, so do not "fix" it here without checking both.
 *
 * Note this is not the same contract as `RelevantArticle`'s grouped-navigation
 * `buildLink`, which returns `{ url, label }`, honours the manual `link_label`
 * and rejects external links outright.
 */
export const resolveRelevantArticleCard = (
  article: RelevantArticle | undefined | null,
  context: RelevantArticleLinkContext,
): RelevantArticleCard | undefined => {
  if (!article) {
    return undefined;
  }

  if (article.external_article) {
    return {
      url: article.external_article.url ?? '',
      title: article.external_article.subtitle ?? '',
      subtitle: article.external_article.description ?? null,
    };
  }

  if (article.article) {
    return {
      url: getInternalArticleUrl(article.article, 'article', context),
      title: article.article.title ?? '',
      subtitle: article.article.subtitle ?? null,
    };
  }

  if (article.page) {
    return {
      url: getInternalArticleUrl(article.page, 'page', context),
      title: article.page.title ?? '',
      subtitle: article.page.subtitle ?? null,
    };
  }

  return undefined;
};
